import { create } from 'zustand';
import api from '../api/client';

interface UserInfo {
  id: number;
  username: string;
  display_name: string | null;
  role: string;
  permissions: string[];
  impersonating?: boolean;
  real_role?: string | null;
  // Заполнены ли 1С логин/пароль пользователя (схема 1c.md §0 rev. 8)
  onec_configured?: boolean;
  // Рабочее место (спека 2026-09-25): имя места, активный оператор, флаг gate
  workplace?: string | null;
  operator?: { id: number; display_name: string | null } | null;
  operator_required?: boolean;
  // Профиль функций места (workplace_auth §4); null у личного логина
  workplace_features?: Record<string, unknown> | null;
  has_pin?: boolean;
}

export interface RoleInfo {
  id: number;
  name: string;
  permissions: string[];
}

interface AuthState {
  user: UserInfo | null;
  loading: boolean;
  roles: RoleInfo[];
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  fetchMe: () => Promise<void>;
  hasPermission: (perm: string) => boolean;
  fetchRoles: () => Promise<void>;
  startImpersonation: (roleId: number) => Promise<void>;
  stopImpersonation: () => Promise<void>;
  switchOperator: (userId: number, pin: string) => Promise<void>;
}

const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  loading: true,
  roles: [],

  login: async (username: string, password: string) => {
    const form = new URLSearchParams();
    form.append('username', username);
    form.append('password', password);
    const res = await api.post('/auth/login', form, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
    localStorage.setItem('access_token', res.data.access_token);
    localStorage.setItem('refresh_token', res.data.refresh_token);
    await get().fetchMe();
  },

  logout: async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // ignore
    }
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('impersonate_role_id');
    set({ user: null, roles: [] });
  },

  fetchMe: async () => {
    try {
      const res = await api.get('/auth/me');
      const data = res.data;
      if (!data || typeof data !== 'object' || !Array.isArray(data.permissions)) {
        throw new Error('Invalid user data');
      }
      set({ user: data, loading: false });
    } catch {
      set({ user: null, loading: false });
    }
  },

  hasPermission: (perm: string) => {
    const user = get().user;
    return user?.permissions?.includes(perm) ?? false;
  },

  fetchRoles: async () => {
    try {
      const res = await api.get('/auth/roles');
      set({ roles: res.data });
    } catch {
      // not admin or error
    }
  },

  startImpersonation: async (roleId: number) => {
    set({ loading: true });
    localStorage.setItem('impersonate_role_id', String(roleId));
    await get().fetchMe();
  },

  stopImpersonation: async () => {
    set({ loading: true });
    localStorage.removeItem('impersonate_role_id');
    await get().fetchMe();
  },

  switchOperator: async (userId: number, pin: string) => {
    const res = await api.post('/auth/switch-operator', { user_id: userId, pin });
    localStorage.setItem('access_token', res.data.access_token);
    localStorage.setItem('refresh_token', res.data.refresh_token);
    localStorage.setItem('last_operator_id', String(userId));
  },
}));

export default useAuthStore;
